<template>
    <Teleport to="body">
        <Transition
            name="loading-fade"
            appear>
            <div
                v-if="visible"
                class="loading-container"
                :class="[`loading-${position}`, { 'loading-overlay': showOverlay }]"
                :style="showOverlay ? overlayStyle : {}"
                @click="handleOverlayClick">
                <Transition
                    name="loading-content-fade"
                    appear>
                    <div
                        class="loading-content n-p-4"
                        @click.stop>
                        <img
                            src="@/assets/global-loading.gif"
                            class="loading-animation"
                            :style="gifStyle"
                            alt="" />
                        <div
                            v-if="text"
                            class="n-mt-3 n-f-1 n-c-1 n-text-center">
                            {{ text }}
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, onUnmounted, computed, type CSSProperties } from 'vue';
import type { LoadingOptions } from './types';

interface LoadingItem extends Required<Omit<LoadingOptions, 'onClose' | 'overlay'>> {
    overlay: {
        color: string;
        opacity: number;
    };
    onClose?: () => void;
    timer?: number;
}

const visible = ref<boolean>(false);
const currentLoading = ref<LoadingItem | null>(null);

const show = (options: LoadingOptions): string => {
    const id = options.id || generateId();

    const loadingItem: LoadingItem = {
        id,
        text: options.text || '',
        animationData: options.animationData || null,
        size: options.size || 150,
        position: options.position || 'center',
        showOverlay: options.showOverlay !== false,
        overlay: {
            color: options.overlay?.color || '#000000',
            opacity: options.overlay?.opacity || 0.5
        },
        clickToClose: options.clickToClose || false,
        duration: options.duration || 0,
        onClose: options.onClose
    };

    currentLoading.value = loadingItem;
    visible.value = true;

    // 自动关闭
    if (loadingItem.duration > 0) {
        loadingItem.timer = window.setTimeout(() => {
            hide();
        }, loadingItem.duration);
    }

    return id;
};

const hide = () => {
    if (currentLoading.value) {
        // 清除定时器
        if (currentLoading.value.timer) {
            clearTimeout(currentLoading.value.timer);
        }

        // 调用关闭回调
        if (currentLoading.value.onClose) {
            currentLoading.value.onClose();
        }
    }

    currentLoading.value = null;
    visible.value = false;
};

const generateId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const text = computed(() => {
    return currentLoading.value?.text || '';
});

const size = computed(() => {
    return currentLoading.value?.size || 80;
});

const gifStyle = computed<CSSProperties>(() => {
    const width = size.value;
    const height = Math.round(width * 0.5);
    return {
        width: `${width}px`,
        height: `${height}px`,
        objectFit: 'cover'
    };
});

const position = computed(() => {
    return currentLoading.value?.position || 'center';
});

const showOverlay = computed(() => {
    return currentLoading.value?.showOverlay !== false;
});

const overlayStyle = computed(() => {
    if (!currentLoading.value) return {};
    return {
        backgroundColor: `${currentLoading.value.overlay.color}${Math.round(currentLoading.value.overlay.opacity * 255)
            .toString(16)
            .padStart(2, '0')}`
    };
});

const handleOverlayClick = () => {
    if (currentLoading.value?.clickToClose) {
        hide();
    }
};

// 暴露方法给外部使用
defineExpose({
    show,
    hide
});

onUnmounted(() => {
    hide();
});
</script>

<style lang="scss" scoped>
.loading-container {
    position: fixed;
    z-index: 10000;
    pointer-events: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}

.loading-overlay {
    position: absolute;
    width: 100vw;
    height: 100vh;
    background-color: var(--global-mask-bg);
}

.loading-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.loading-top {
    top: 20%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.loading-bottom {
    bottom: 20%;
    left: 50%;
    transform: translate(-50%, 50%);
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: var(--global-mask-bg);
    border-radius: var(--radius-10);
}

.loading-animation {
    width: 200px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Fade动画 - 背景遮罩 */
.loading-fade-enter-active {
    transition: opacity 0.3s ease;
}

.loading-fade-leave-active {
    transition: opacity 0.3s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
    opacity: 0;
}

/* Fade动画 - 内容区域 */
.loading-content-fade-enter-active {
    transition: all 0.3s ease;
    transition-delay: 0.1s;
}

.loading-content-fade-leave-active {
    transition: all 0.2s ease;
}

.loading-content-fade-enter-from {
    opacity: 0;
    transform: scale(0.9);
}

.loading-content-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}
</style>
